import { openDB } from 'idb';
import type { Corpus, Section, SearchResult, Unit } from '../models/Corpus';
import { parseHtmlCorpus } from './HtmlCorpusLoader';

export class LocalCorpus implements Corpus {
    id: string;
    title: string;
    author: string;
    language: string;
    source: { type: 'html' | 'epub' | 'internal'; path?: string; };

    private units: Unit[] = [];
    private _error: string | null = null;
    private _ready = false;
    // In-memory simple search index: token -> list of location strings
    private searchIndex: Map<string, Set<string>> = new Map();

    constructor(meta: { id: string; title: string; author: string; language: string; source: any }) {
        this.id = meta.id;
        this.title = meta.title;
        this.author = meta.author;
        this.language = meta.language;
        this.source = meta.source;
    }

    async load(): Promise<boolean> {
        if (this._ready) return true;
        if (!this.source.path) {
            this._error = "No source path";
            return false;
        }

        try {
            const resp = await fetch(this.source.path);
            if (!resp.ok) throw new Error(`Failed to fetch ${this.source.path}`);
            const text = await resp.text();

            // Parse
            this.units = await parseHtmlCorpus(text);

            await this.loadOrBuildIndex();

            this._ready = true;
            return true;
        } catch (e: any) {
            console.error(`Failed to load corpus ${this.id}`, e);
            this._error = e.message;
            return false;
        }
    }

    private async loadOrBuildIndex() {
        if (this.units.length === 0) return;

        try {
            const db = await openDB('corpora_index_v1', 1, {
                upgrade(db) {
                    db.createObjectStore('indices');
                },
            });

            const cached = await db.get('indices', this.id);
            if (cached && cached.timestamp && cached.index) {
                // Deserialize
                this.searchIndex = cached.index; // idb stores Map properly? structuredClone supports Map.
                // Wait, idb (IndexedDB) structured cloning usually handles Map in modern browsers,
                // BUT just in case lets verify. 
                // However, basic JSON.stringify does NOT handle Map.
                // If idb uses structuredClone (which it does), Maps are preserved.
                // Let's assume modern environment.
                console.log(`[${this.id}] Loaded index from cache`);
                return;
            }
        } catch (e) {
            console.warn("Failed to load index from IDB", e);
        }

        console.log(`[${this.id}] Building index...`);
        this.buildIndex();
        this.saveIndex();
    }

    // Explicit rebuild command
    async reindex() {
        console.log(`[${this.id}] Rebuilding index...`);
        this.buildIndex();
        await this.saveIndex();
    }

    private async saveIndex() {
        try {
            const db = await openDB('corpora_index_v1', 1);
            await db.put('indices', {
                index: this.searchIndex,
                timestamp: Date.now()
            }, this.id);
            console.log(`[${this.id}] Index saved.`);
        } catch (e) {
            console.warn("Failed to save index to IDB", e);
        }
    }

    private buildIndex() {
        // Tokenize and store mapping to unit_index:paragraph_key
        this.searchIndex.clear();
        this.units.forEach((unit, uIdx) => {
            const content = unit.content || {};
            // If empty content, skip
            if (Object.keys(content).length === 0) return;

            Object.entries(content).forEach(([key, text]) => {
                const tokens = text.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(t => t.length > 2);
                const loc = `${uIdx + 1}:${key}`; // Chapter:Para
                tokens.forEach(t => {
                    if (!this.searchIndex.has(t)) this.searchIndex.set(t, new Set());
                    this.searchIndex.get(t)?.add(loc);
                });
            });
        });
    }

    getError() { return this._error; }

    getSections(): Section[] {
        // Flat corpus -> Single "Book"
        return [{
            id: 'main',
            title: this.title,
            chapterCount: this.units.length
        }];
    }

    getChapter(_sectionId: string, chapter: number): Record<string, string> | null {
        // Chapter is 1-based
        const unit = this.units[chapter - 1];
        return unit ? (unit.content || {}) : null;
    }

    getUnitText(_sectionId: string, chapter: number): string | null {
        const unit = this.units[chapter - 1];
        return unit ? unit.text : null;
    }

    async search(query: string): Promise<SearchResult[]> {
        const tokens = query.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(t => t.length > 2);
        if (tokens.length === 0) return [];

        let resultLocs: Set<string> | null = null;

        // Intersection
        for (const t of tokens) {
            const hits = this.searchIndex.get(t);
            if (!hits) return []; // No full match

            if (!resultLocs) {
                resultLocs = new Set(hits);
            } else {
                const intersect = new Set<string>();
                for (const h of hits) {
                    if (resultLocs.has(h)) intersect.add(h);
                }
                resultLocs = intersect;
            }
            if (resultLocs.size === 0) return [];
        }

        if (!resultLocs) return [];

        const results: SearchResult[] = [];
        // hydration
        Array.from(resultLocs).slice(0, 50).forEach(loc => {
            const [chStr, paraStr] = loc.split(':');
            const ch = parseInt(chStr);
            const unit = this.units[ch - 1];
            if (unit && unit.content) {
                const text = unit.content[paraStr] || '';
                results.push({
                    vid: `${this.id}:main:${ch}:${paraStr}`,
                    bookId: 'main',
                    bookName: this.title,
                    chapter: ch,
                    verse: parseInt(paraStr),
                    text
                });
            }
        });

        return results;
    }

    resolveSectionId(slug: string): string | undefined {
        const norm = slug.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (norm.includes('main') || norm === this.title.toLowerCase().replace(/[^a-z0-9]/g, '')) return 'main';
        return undefined;
    }
}
